// Sound Service - Handles all game audio using Howler.js
import { Howl, Howler } from 'howler';

// Sound definitions with categories and file paths
// Using OGG (Kenney.nl CC0 sounds) with MP3 fallback
const SOUND_DEFINITIONS = {
  // Card Actions - Kenney Casino Audio Pack (CC0)
  card_play_single: { src: ['/sounds/card/play_single.ogg', '/sounds/card/play_single.mp3'], category: 'sfx', volume: 0.6 },
  card_play_multi: { src: ['/sounds/card/play_multi.ogg', '/sounds/card/play_multi.mp3'], category: 'sfx', volume: 0.9 },
  card_draw: { src: '/sounds/card/card_flip.mp3', category: 'sfx', volume: 0.5 },
  card_pass: { src: '/sounds/card/pass.mp3', category: 'sfx', volume: 0.4 },
  card_select: { src: ['/sounds/card/select.ogg', '/sounds/card/select.mp3'], category: 'ui', volume: 0.3 },
  card_deselect: { src: ['/sounds/card/deselect.ogg', '/sounds/card/deselect.mp3'], category: 'ui', volume: 0.25 },
  card_deal: { src: ['/sounds/card/deal.ogg', '/sounds/card/deal.mp3'], category: 'sfx', volume: 0.5 },
  card_spread: { src: ['/sounds/card/deal.ogg', '/sounds/card/deal.mp3'], category: 'sfx', volume: 0.7, rate: 1.3 },
  card_shuffle: { src: ['/sounds/card/shuffle.ogg', '/sounds/card/shuffle.mp3'], category: 'sfx', volume: 0.6 },
  card_flying: { src: '/sounds/card/flying.mp3', category: 'sfx', volume: 0.8 },

  // Trick Outcomes
  trick_win: { src: '/sounds/trick/win.mp3', category: 'sfx', volume: 0.7 },
  trick_lose: { src: '/sounds/trick/lose.mp3', category: 'sfx', volume: 0.4 },
  trick_combo_pair: { src: '/sounds/trick/combo_pair.mp3', category: 'sfx', volume: 0.5 },
  trick_combo_triple: { src: '/sounds/trick/combo_triple.mp3', category: 'sfx', volume: 0.6 },
  trick_combo_straight: { src: '/sounds/trick/combo_straight.mp3', category: 'sfx', volume: 0.7 },
  trick_combo_royal: { src: '/sounds/trick/combo_royal.mp3', category: 'sfx', volume: 0.8 },

  // Shop Actions - Kenney Casino Audio Pack (CC0)
  shop_open: { src: '/sounds/shop/open.mp3', category: 'sfx', volume: 0.5 },
  shop_buy_seal: { src: ['/sounds/shop/buy_seal.ogg', '/sounds/shop/buy_seal.mp3'], category: 'sfx', volume: 0.6 },
  shop_buy_joker: { src: ['/sounds/shop/buy_joker.ogg', '/sounds/shop/buy_joker.mp3'], category: 'sfx', volume: 0.6 },
  shop_sell: { src: ['/sounds/shop/sell.ogg', '/sounds/shop/sell.mp3'], category: 'sfx', volume: 0.5 },
  shop_reroll: { src: '/sounds/shop/reroll.mp3', category: 'sfx', volume: 0.5 },
  shop_cant_afford: { src: '/sounds/shop/cant_afford.mp3', category: 'ui', volume: 0.5 },
  booster_open: { src: '/sounds/shop/booster_open.mp3', category: 'sfx', volume: 0.7 },

  // Seal Effects - Kenney Casino Audio Pack (CC0)
  seal_trigger: { src: '/sounds/seal/trigger.mp3', category: 'sfx', volume: 0.6 },
  seal_coins: { src: ['/sounds/seal/coins.ogg', '/sounds/seal/coins.mp3'], category: 'sfx', volume: 0.5 },
  seal_steal: { src: '/sounds/seal/steal.mp3', category: 'sfx', volume: 0.6 },
  seal_extra_play: { src: '/sounds/seal/extra_play.mp3', category: 'sfx', volume: 0.6 },
  seal_attach: { src: '/sounds/seal/attach.mp3', category: 'sfx', volume: 0.4 },

  // Game Flow
  game_start: { src: '/sounds/game/start.mp3', category: 'sfx', volume: 0.7 },
  round_start: { src: '/sounds/game/round_start.mp3', category: 'sfx', volume: 0.5 },
  round_end: { src: '/sounds/game/round_end.mp3', category: 'sfx', volume: 0.6 },
  player_finished: { src: '/sounds/game/player_finished.mp3', category: 'sfx', volume: 0.6 },
  game_win: { src: '/sounds/game/win.mp3', category: 'sfx', volume: 0.8 },
  game_lose: { src: '/sounds/game/lose.mp3', category: 'sfx', volume: 0.6 },
  exchange_phase: { src: '/sounds/game/exchange.mp3', category: 'sfx', volume: 0.5 },
  turn_notification: { src: '/sounds/game/turn.mp3', category: 'notification', volume: 0.5 },

  // UI Feedback
  ui_click: { src: '/sounds/ui/click.mp3', category: 'ui', volume: 0.3 },
  button_click: { src: '/sounds/ui/click.mp3', category: 'ui', volume: 0.3 }, // Alias
  error: { src: '/sounds/ui/error.mp3', category: 'ui', volume: 0.5 },
  success: { src: '/sounds/ui/success.mp3', category: 'ui', volume: 0.5 },
  timer_warning: { src: '/sounds/ui/timer_warning.mp3', category: 'notification', volume: 0.6 },
  timer_tick: { src: '/sounds/ui/timer_tick.mp3', category: 'ui', volume: 0.3 },

  // Lobby/Connection Events
  player_join: { src: ['/sounds/ui/player_join.ogg', '/sounds/ui/player_join.mp3'], category: 'notification', volume: 0.5 },
  player_leave: { src: ['/sounds/ui/player_leave.ogg', '/sounds/ui/player_leave.mp3'], category: 'notification', volume: 0.4 },
  connection_lost: { src: ['/sounds/ui/connection_lost.ogg', '/sounds/ui/connection_lost.mp3'], category: 'notification', volume: 0.6 },
  connection_restored: { src: ['/sounds/ui/connection_restored.ogg', '/sounds/ui/connection_restored.mp3'], category: 'notification', volume: 0.5 },

  // Joker Effects
  joker_activate: { src: '/sounds/joker/activate.mp3', category: 'sfx', volume: 0.6 },
  joker_bonus: { src: '/sounds/joker/bonus.mp3', category: 'sfx', volume: 0.3 },
  joker_bonus2: { src: '/sounds/joker/bonus2.mp3', category: 'sfx', volume: 0.3 },
  joker_multiply: { src: ['/sounds/joker/multiply.ogg', '/sounds/joker/multiply.mp3'], category: 'sfx', volume: 0.6 },
  joker_coins: { src: ['/sounds/joker/coins.ogg', '/sounds/joker/coins.mp3'], category: 'sfx', volume: 0.5 },
  joker_legendary: { src: ['/sounds/joker/legendary.ogg', '/sounds/joker/legendary.mp3'], category: 'sfx', volume: 0.8 },
  joker_fart_1: { src: '/sounds/joker/fart.mp3', category: 'sfx', volume: 0.7 },
  joker_fart_2: { src: '/sounds/joker/fart2.mp3', category: 'sfx', volume: 0.7 },
  joker_fart_3: { src: '/sounds/joker/fart3.mp3', category: 'sfx', volume: 0.7 },
  joker_fart_4: { src: '/sounds/joker/fart4.mp3', category: 'sfx', volume: 0.7 },
  joker_fart_5: { src: '/sounds/joker/fart5.mp3', category: 'sfx', volume: 0.7 },

  // Advanced Seal Effects
  seal_freeze: { src: ['/sounds/seal/freeze.ogg', '/sounds/seal/freeze.mp3'], category: 'sfx', volume: 0.5 },
  seal_burn: { src: ['/sounds/seal/burn.ogg', '/sounds/seal/burn.mp3'], category: 'sfx', volume: 0.5 },
  seal_mirror: { src: ['/sounds/seal/mirror.ogg', '/sounds/seal/mirror.mp3'], category: 'sfx', volume: 0.5 },
  seal_shadow: { src: ['/sounds/seal/shadow.ogg', '/sounds/seal/shadow.mp3'], category: 'sfx', volume: 0.4 },
  seal_shield: { src: ['/sounds/seal/shield.ogg', '/sounds/seal/shield.mp3'], category: 'sfx', volume: 0.5 },
  seal_poison: { src: ['/sounds/seal/poison.ogg', '/sounds/seal/poison.mp3'], category: 'sfx', volume: 0.4 },
  seal_heal: { src: ['/sounds/seal/heal.ogg', '/sounds/seal/heal.mp3'], category: 'sfx', volume: 0.5 },
  seal_curse: { src: ['/sounds/seal/curse.ogg', '/sounds/seal/curse.mp3'], category: 'sfx', volume: 0.5 },
  glass_break: { src: ['/sounds/seal/glass_break.ogg', '/sounds/seal/glass_break.mp3'], category: 'sfx', volume: 0.7 },
  glass_success: { src: ['/sounds/seal/glass_success.ogg', '/sounds/seal/glass_success.mp3'], category: 'sfx', volume: 0.5 },

  // Magic Card Effects
  magic_card_draw: { src: ['/sounds/joker/legendary.ogg', '/sounds/joker/legendary.mp3'], category: 'sfx', volume: 0.8 },

  // Minigame Sounds
  minigame_catch: { src: '/sounds/minigame/catch.mp3', category: 'sfx', volume: 0.7 },
  minigame_woo: { src: '/sounds/minigame/woo.mp3', category: 'sfx', volume: 0.6 },
  minigame_button: { src: '/sounds/minigame/button.mp3', category: 'sfx', volume: 0.4 },
  minigame_result: { src: '/sounds/ui/success.mp3', category: 'sfx', volume: 0.5 },
};

class SoundService {
  constructor() {
    this.sounds = {};
    this.masterVolume = 0.7;
    this.categoryVolumes = {
      sfx: 1.0,
      ui: 0.8,
      notification: 1.0
    };
    this.muted = false;
    this.initialized = false;
    this.audioUnlocked = false;
    this.failedSounds = new Set();

    this.loadSettings();
    this.setupAudioUnlock();
  }

  // Load settings from localStorage
  loadSettings() {
    try {
      const saved = localStorage.getItem('soundSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.masterVolume = settings.masterVolume ?? 0.7;
        this.categoryVolumes = settings.categoryVolumes ?? this.categoryVolumes;
        this.muted = settings.muted ?? false;
      }
    } catch (e) {
      console.warn('Could not load sound settings:', e);
    }
    this.applyGlobalVolume();
  }

  // Save settings to localStorage
  saveSettings() {
    try {
      localStorage.setItem('soundSettings', JSON.stringify({
        masterVolume: this.masterVolume,
        categoryVolumes: this.categoryVolumes,
        muted: this.muted
      }));
    } catch (e) {
      console.warn('Could not save sound settings:', e);
    }
  }

  // Apply volume to all sound effects
  // Note: We don't use Howler.volume() because it affects ALL Howler instances including music
  applyGlobalVolume() {
    // Volume is applied per-sound in the play() method
    // This method is kept for backwards compatibility but no longer uses global Howler volume
  }

  // Setup audio unlock for mobile browsers
  setupAudioUnlock() {
    const unlock = () => {
      if (this.audioUnlocked) return;

      // Create and play a silent buffer to unlock audio
      const ctx = Howler.ctx;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }

      this.audioUnlocked = true;
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('touchend', unlock);
      document.removeEventListener('click', unlock);
    };

    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('touchend', unlock, { once: true });
    document.addEventListener('click', unlock, { once: true });
  }

  // Initialize all sounds (lazy loading)
  init() {
    if (this.initialized) return;

    Object.entries(SOUND_DEFINITIONS).forEach(([id, def]) => {
      // Handle both string and array src formats
      const srcArray = Array.isArray(def.src) ? def.src : [def.src];
      this.sounds[id] = {
        ...def,
        howl: new Howl({
          src: srcArray,
          volume: def.volume,
          preload: false, // Lazy load
          onloaderror: (soundId, error) => {
            this.failedSounds.add(id);
            // Silently fail - sounds are optional
          }
        })
      };
    });

    this.initialized = true;
  }

  // Preload critical sounds
  preload(soundIds = ['card_play_single', 'card_select', 'trick_win', 'error', 'minigame_catch']) {
    this.init();
    soundIds.forEach(id => {
      if (this.sounds[id] && !this.failedSounds.has(id)) {
        this.sounds[id].howl.load();
      }
    });
  }

  // Play a sound by ID
  play(soundId, options = {}) {
    if (this.muted) return null;
    if (this.failedSounds.has(soundId)) return null;

    this.init();

    const soundDef = this.sounds[soundId];
    if (!soundDef) {
      console.warn(`Sound not found: ${soundId}`);
      return null;
    }

    try {
      const categoryVolume = this.categoryVolumes[soundDef.category] ?? 1;
      const finalVolume = (options.volume ?? soundDef.volume) * categoryVolume;
      const rate = options.rate ?? soundDef.rate ?? 1.0;

      soundDef.howl.volume(finalVolume);
      soundDef.howl.rate(rate);

      // Load if not loaded yet
      if (soundDef.howl.state() === 'unloaded') {
        soundDef.howl.load();
      }

      // Ensure AudioContext is running (might be suspended on mobile)
      const ctx = Howler.ctx;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }

      return soundDef.howl.play();
    } catch (error) {
      console.warn(`Error playing sound ${soundId}:`, error);
      return null;
    }
  }

  // Play combo sound based on play type
  playComboSound(playType) {
    const comboSounds = {
      'pair': 'trick_combo_pair',
      'triple': 'trick_combo_triple',
      'twoPairs': 'trick_combo_pair',
      'straight': 'trick_combo_straight',
      'flush': 'trick_combo_straight',
      'fullHouse': 'trick_combo_straight',
      'pokerWithKicker': 'trick_combo_royal',
      'straightFlush': 'trick_combo_royal',
      'royalFlush': 'trick_combo_royal'
    };

    const soundId = comboSounds[playType];
    if (soundId) {
      this.play(soundId);
    }
  }

  // Set master volume (0.0 - 1.0)
  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.applyGlobalVolume();
    this.saveSettings();
  }

  // Get master volume
  getMasterVolume() {
    return this.masterVolume;
  }

  // Set category volume (0.0 - 1.0)
  setCategoryVolume(category, volume) {
    this.categoryVolumes[category] = Math.max(0, Math.min(1, volume));
    this.saveSettings();
  }

  // Get category volume
  getCategoryVolume(category) {
    return this.categoryVolumes[category] ?? 1;
  }

  // Toggle mute
  toggleMute() {
    this.muted = !this.muted;
    this.applyGlobalVolume();
    this.saveSettings();
    return this.muted;
  }

  // Set muted state
  setMuted(muted) {
    this.muted = muted;
    this.applyGlobalVolume();
    this.saveSettings();
  }

  // Check if muted
  isMuted() {
    return this.muted;
  }

  // Stop all sounds
  stopAll() {
    Howler.stop();
  }

  // Get all settings for UI
  getSettings() {
    return {
      masterVolume: this.masterVolume,
      categoryVolumes: { ...this.categoryVolumes },
      muted: this.muted
    };
  }
}

// Singleton instance
const soundService = new SoundService();
export default soundService;
